import { apiClient } from 'config/api';
import { PaginationMetadata, User } from './useGetAllUsersPaginated';

interface UsersListResponse {
  data: User[];
  pagination: PaginationMetadata;
}

/** Page size used only for bulk loading when filtering */
const BULK_PAGE_SIZE = 100;
/** Max parallel requests to avoid overloading the API */
const MAX_CONCURRENT_REQUESTS = 5;

/**
 * Fetches a single users page
 * @param page - Page number
 * @param limit - Page size
 * @returns Users of the requested page
 */
async function fetchUsersPage(page: number, limit: number): Promise<User[]> {
  const { data } = await apiClient.get<UsersListResponse>('/auth0/user', {
    params: { page, limit },
  });

  return data?.data || [];
}

/**
 * Fetches all users in parallel batches to speed up filtering
 * @param totalCount - Total amount of users reported by pagination
 * @returns All users
 */
async function getAllUsers(totalCount: number): Promise<User[]> {
  const totalPages = Math.max(1, Math.ceil(totalCount / BULK_PAGE_SIZE));
  const pages = Array.from({ length: totalPages }, (_, index) => index + 1);
  const users: User[] = [];

  for (let index = 0; index < pages.length; index += MAX_CONCURRENT_REQUESTS) {
    const batch = pages.slice(index, index + MAX_CONCURRENT_REQUESTS);
    const batchUsers = await Promise.all(batch.map((page) => fetchUsersPage(page, BULK_PAGE_SIZE)));
    users.push(...batchUsers.flat());
  }

  return users;
}

export default getAllUsers;
